/** One run of a snippet, flagged when it matches the searched term. */
interface TextSegment {
  text: string
  match: boolean
}

const REGEX_SPECIALS = /[.*+?^${}()|[\]\\]/g

/**
 * Case-insensitive matcher for the searched words. The search endpoint returns
 * snippets with no markup ("locate it client-side"), and matches word-prefixes
 * (`bugun` finds `bugungi`), so each whitespace-separated word is matched from a
 * word boundary onward. Returns null when there's nothing to highlight.
 */
function termPattern(query: string): RegExp | null {
  const terms = query
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((term) => term.replace(REGEX_SPECIALS, '\\$&'))
  if (terms.length === 0) return null
  // Longest first, so "ford f150" prefers the longer overlapping run.
  terms.sort((a, b) => b.length - a.length)
  return new RegExp(`\\b(?:${terms.join('|')})`, 'gi')
}

/**
 * Split `text` into alternating plain / matched runs so the searched term can be
 * emphasised inside a result snippet. Falls back to a single plain run when the
 * query has no usable words.
 */
export function highlightTerms(text: string, query: string): TextSegment[] {
  const pattern = termPattern(query)
  if (!pattern || !text) return [{ text, match: false }]

  const segments: TextSegment[] = []
  let index = 0
  for (const found of text.matchAll(pattern)) {
    const start = found.index
    if (start > index) {
      segments.push({ text: text.slice(index, start), match: false })
    }
    segments.push({ text: found[0], match: true })
    index = start + found[0].length
  }
  if (index < text.length) {
    segments.push({ text: text.slice(index), match: false })
  }
  return segments
}
